import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Api from "src/Config";
import { createContext, FunctionComponent, ReactElement, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { textTruncate } from "src/helpers/truncateText";
import { format } from "date-fns";

export const TeamsContext = createContext(null);

type Props = {
  children: ReactElement;
};

const TeamsProvider: FunctionComponent<Props> = ({ children }) => {
  const [queryParams, setQueryParams] = useSearchParams();
  const [teamMemberQueryParams, setTeamMemberQueryParams] = useSearchParams();
  const [teamTransactionQueryParams, setTeamTransactionQueryParams] =
    useSearchParams();

  const pageQuery = queryParams.get("page") || 1;
  const teamMemberPageQuery = teamMemberQueryParams.get("page") || 1;
  const teamTransactionPageQuery = teamTransactionQueryParams.get("page") || 1;

  const [searchParam, setSearchParam] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchTeamParam, setSearchTeamParam] = useState("");
  const [searchTeamValue, setSearchTeamValue] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [teamMemberPageNumber, setTeamMemberPageNumber] = useState(0);
  const [teamTransactionPageNumber, setTeamTransactionPageNumber] = useState(0);

  const [pageCount, setPageCount] = useState(0);
  const [teamMemberPageCount, setTeamMemberPageCount] = useState(0);
  const [teamTransactionPageCount, setTeamTransactionPageCount] = useState(0);
  const [paramId, setParamId] = useState("");
  const [paramView, setParamView] = useState("");

  const params = new URLSearchParams(queryParams);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["teams", pageQuery, searchTeamValue],
    staleTime: 30000,
    queryFn: async () => {
      const url = searchTeamValue
        ? `settings/tm/case-management/groups?search=${searchTeamValue}&page_size=10&page=1`
        : `settings/tm/case-management/groups?page_size=10&${decodeURIComponent(
            queryParams as any,
          )}`;

      const { data } = await Api.get(`${url}`);

      const response = data.results.map(
        (res: {
          id: string;
          name: string;
          member_count: number;
          assigned_transactions_count: number;
          transaction_count: number;
          created_at: string;
          users: any;
        }) => ({
          id: res.id,
          team: `${textTruncate(`${res.name || ""}`, 20)}`,
          "number of team members": res?.member_count,
          "date of creation": format(new Date(res.created_at), "dd/MM/yyyy"),
          "assigned transactions": res?.assigned_transactions_count,
          members: res?.users,
        }),
      );

      return {
        response,
        metadata: data?.metadata,
      };
    },
  });

  const {
    data: teamsTransactionData,
    isLoading: isLoadingTeamsTransaction,
    error: teamsTransactionError,
    isFetching: isFetchingTeamsTransaction,
  } = useQuery({
    queryKey: [
      "teams_transaction",
      teamTransactionPageQuery,
      paramId,
      paramView,
    ],
    staleTime: 30000,
    enabled: paramView === "assigned-transactions",
    queryFn: async () => {
      const url = `tm/case-management/groups/${paramId}/transactions`;
      const { data } = await Api.get(`${url}`);

      return {
        response: data.results,
        metadata: data?.metadata,
      };
    },
  });

  const {
    data: teamMembersData,
    isLoading: isLoadingTeamMembers,
    error: teamMembersError,
    isFetching: isFetchingTeamMembers,
  } = useQuery({
    queryKey: [
      "team_members",
      teamMemberPageQuery,
      paramId,
      paramView,
      searchValue,
    ],
    staleTime: 30000,
    enabled: paramView === "team-members",
    queryFn: async () => {
      const url = searchValue
        ? `settings/tm/case-management/groups/${paramId}/users?search=${searchValue}&page_size=10&page=1`
        : `settings/tm/case-management/groups/${paramId}/users?page_size=10&${decodeURIComponent(
            queryParams as any,
          )}`;

      // const url = `settings/tm/case-management/groups/${paramId}/users?search=${searchParam}`;
      const { data } = await Api.get(`${url}`);

      return {
        response: data?.results,
        metadata: data?.metadata,
      };
    },
  });

  const {
    mutateAsync: mutateAsyncDeleteMember,
    data: deleteMemberData,
    isPending: deleteMemberIsPending,
    error: deleteMemberError,
  } = useMutation({
    mutationFn: async (payload: any) => {
      const data = await Api.delete(
        `settings/tm/case-management/groups/${payload.group_id}/remove-user`,
        { data: payload },
      );
      return data;
    },
    onError: (err) => {
      throw err;
    },
  });

  const {
    mutateAsync: mutateAsyncBulkUnassignTransaction,
    data: bulkUnassignTransactionData,
    isPending: isPendingBulkUnassignTransaction,
    error: bulkUnassignTransactionError,
  } = useMutation({
    mutationFn: async (payload: any) => {
      const { transactions, group_id } = payload;
      const data = await Api.put(
        `tm/case-management/groups/${group_id}/transactions/bulk-unassign`,
        {
          transactions,
        },
      );
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
    },
  });

  const {
    mutateAsync: mutateAsyncBulkUnassignMembers,
    data: bulkUnassignMembersData,
    isPending: isPendingBulkUnassignMembers,
    error: bulkUnassignMembersError,
  } = useMutation({
    mutationFn: async (payload: any) => {
      const data = await Api.put(
        `settings/tm/case-management/groups/${params.get(
          "tid",
        )}/remove-bulk-users`,
        {
          ...payload,
        },
      );
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
    },
  });

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.metadata.count / 10));
    }
  }, [data]);

  useEffect(() => {
    if (teamMembersData) {
      setTeamMemberPageCount(Math.ceil(teamMembersData?.metadata?.count / 10));
    }
  }, [teamMembersData]);

  useEffect(() => {
    if (teamsTransactionData) {
      setTeamTransactionPageCount(
        Math.ceil(teamsTransactionData?.metadata?.count / 10),
      );
    }
  }, [teamsTransactionData]);

  useEffect(() => {
    if (params.get("view") && params.get("tid")) {
      setParamId(params.get("tid"));
      setParamView(params.get("view"));
    } else {
      setParamId("");
      setParamView("");
    }
  }, [params.get("view"), params.get("tid")]);

  const handleSearch = (e: any) => {
    if (e.target.value) {
      setSearchParam(e.target.value);
    } else {
      setSearchValue("");
      setSearchParam("");
    }
  };

  const handleSearchTeam = (e: any) => {
    if (e.target.value) {
      setSearchTeamParam(e.target.value);
    } else {
      setSearchTeamValue("");
      setSearchTeamParam("");
    }
  };

  const handleSearchRecord = () => {
    setSearchValue(searchParam);
  };

  const handleSearchTeamRecord = () => {
    setSearchTeamValue(searchTeamParam);
  };

  const contextValue = useMemo(
    () => ({
      data,
      isLoading,
      handleSearch,
      handleSearchRecord,
      handleSearchTeam,
      handleSearchTeamRecord,
      searchParam,
      searchTeamParam,
      isFetching,
      pageNumber,
      teamMemberPageNumber,
      teamTransactionPageNumber,
      pageCount,
      teamMemberPageCount,
      teamTransactionPageCount,
      setPageNumber,
      setTeamMemberPageNumber,
      setTeamTransactionPageNumber,
      pageQuery,
      teamMemberPageQuery,
      teamTransactionPageQuery,
      setQueryParams,
      setTeamMemberQueryParams,
      setTeamTransactionQueryParams,
      teamsTransactionData,
      isLoadingTeamsTransaction,
      teamsTransactionError,
      isFetchingTeamsTransaction,

      mutateAsyncDeleteMember,
      deleteMemberData,
      deleteMemberIsPending,
      deleteMemberError,

      teamMembersData,
      isLoadingTeamMembers,
      teamMembersError,
      isFetchingTeamMembers,

      mutateAsyncBulkUnassignTransaction,
      bulkUnassignTransactionData,
      isPendingBulkUnassignTransaction,
      bulkUnassignTransactionError,

      mutateAsyncBulkUnassignMembers,
      bulkUnassignMembersData,
      isPendingBulkUnassignMembers,
      bulkUnassignMembersError,
    }),
    [
      isLoading,
      data,
      error,
      handleSearch,
      handleSearchRecord,
      handleSearchTeam,
      handleSearchTeamRecord,
      searchParam,
      searchTeamParam,
      isFetching,
      pageNumber,
      teamMemberPageNumber,
      teamTransactionPageNumber,
      pageCount,
      teamMemberPageCount,
      teamTransactionPageCount,
      setPageNumber,
      setTeamMemberPageNumber,
      setTeamTransactionPageNumber,
      pageQuery,
      teamMemberPageQuery,
      teamTransactionPageQuery,
      setQueryParams,
      setTeamMemberQueryParams,
      setTeamTransactionQueryParams,
      teamsTransactionData,
      isLoadingTeamsTransaction,
      teamsTransactionError,
      isFetchingTeamsTransaction,
      mutateAsyncDeleteMember,
      deleteMemberData,
      deleteMemberIsPending,
      deleteMemberError,
      teamMembersData,
      isLoadingTeamMembers,
      teamMembersError,
      isFetchingTeamMembers,
      mutateAsyncBulkUnassignTransaction,
      bulkUnassignTransactionData,
      isPendingBulkUnassignTransaction,
      bulkUnassignTransactionError,

      mutateAsyncBulkUnassignMembers,
      bulkUnassignMembersData,
      isPendingBulkUnassignMembers,
      bulkUnassignMembersError,
    ],
  );
  return (
    <TeamsContext.Provider value={contextValue}>
      {children}
    </TeamsContext.Provider>
  );
};

export default TeamsProvider;
